import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Alert } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import moment from 'moment-timezone';
import { RootState } from '../../../setup';
import { useSelector, useDispatch } from 'react-redux';
import { setTimeZone } from '../../../setup/redux/timeZone/reducer';
import tzlookup from 'tz-lookup';

const selectTimeZone = (state: RootState) => state.timeZone.timeZone;

const USATimezones = [
  { id: 'America/New_York', name: 'Eastern' },
  { id: 'America/Chicago', name: 'Central' },
  { id: 'America/Denver', name: 'Mountain' },
  { id: 'America/Los_Angeles', name: 'Pacific' },
  { id: 'America/Anchorage', name: 'Alaska' },
  { id: 'America/Phoenix', name: 'Mountain (No DST)' },
  { id: 'America/Adak', name: 'Hawaii-Aleutian' },
  { id: 'Pacific/Honolulu', name: 'Hawaii' }
];

export default function TimezoneSelector({ onTimezoneChange }) {
  const dispatch = useDispatch();
  const timeZone = useSelector(selectTimeZone);
  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [modalTimezone, setModalTimezone] = useState(null); // Temporary state for modal
  const [geoTimezone, setGeoTimezone] = useState(null);
  const [showMismatchModal, setShowMismatchModal] = useState(false);
  const [isLoadingGeo, setIsLoadingGeo] = useState(false);

  const getTimezoneOffsetLabel = (timezone) => {
    const offset = moment.tz(timezone.id).utcOffset() / 60;
    const offsetSign = offset >= 0 ? '+' : '-';
    return `${timezone.name} (UTC${offsetSign}${Math.abs(offset)})`;
  };

  const detectGeoTimezone = async () => {
    setIsLoadingGeo(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          try {
            const lat = pos.coords.latitude;
            const lon = pos.coords.longitude;
            const guessedTz = tzlookup(lat, lon);
            setGeoTimezone(guessedTz);
          } catch (tzError) {
            try {
              const fallback = moment.tz.guess();
              setGeoTimezone(fallback);
            } catch (fallbackErr) {
              setGeoTimezone(null);
            }
          } finally {
            setIsLoadingGeo(false);
          }
        },
        (geoErr) => {
          // Fallback to moment.tz.guess() when geolocation is denied
          try {
            const fallback = moment.tz.guess();
            setGeoTimezone(fallback);
          } catch (fallbackErr) {
            console.warn("Fallback timezone detection failed:", fallbackErr);
            setGeoTimezone(null);
          } finally {
            setIsLoadingGeo(false);
          }
        },
        { 
          timeout: 8000,
          enableHighAccuracy: true, 
          maximumAge: 300000 
        }
      );
    } else {
      // Geolocation not supported, try moment.tz.guess()
      try {
        const fallback = moment.tz.guess();
        setGeoTimezone(fallback);
      } catch (fallbackErr) {
        console.warn("Moment timezone detection failed:", fallbackErr);
        setGeoTimezone(null);
      } finally {
        setIsLoadingGeo(false);
      }
    }
  };

  // Check if geolocation timezone matches selected timezone
  const isTimezoneMismatch = () => {
    if (!geoTimezone || !selectedTimezone) return false;
    return geoTimezone !== selectedTimezone.id;
  };

  // Handle timezone change from modal
  const handleModalTimezoneChange = (newTimezone) => {
    setSelectedTimezone(newTimezone);
    onTimezoneChange(newTimezone.id);
    dispatch(setTimeZone(newTimezone.id));
    setModalTimezone(null); // Reset modal timezone
    setShowMismatchModal(false);
  };

  useEffect(() => {
    detectGeoTimezone();
  }, []);

  useEffect(() => {
    const matchedTimezone = USATimezones.find((tz) => tz.id === timeZone);
    if (matchedTimezone) {
      setSelectedTimezone(matchedTimezone);
    }
  }, [timeZone]);

  const handleTimezoneChange = (event, newValue) => {
    setSelectedTimezone(newValue);
    onTimezoneChange(newValue.id);
  };

  const getGeoTimezoneDisplayName = () => {
    if (!geoTimezone) return 'Unknown';
    const matchedTimezone = USATimezones.find((tz) => tz.id === geoTimezone);
    return matchedTimezone ? getTimezoneOffsetLabel(matchedTimezone) : geoTimezone;
  };

  // Check if detected timezone is in the USATimezones list
  const isDetectedTimezoneInList = () => {
    if (!geoTimezone) return false;
    return USATimezones.some((tz) => tz.id === geoTimezone);
  };

  return (
    <>
      <div className="d-flex align-items-center justify-content-between" style={{ width: '100%' }}>
        <div style={{ position: 'relative', width: '100%' }}>
          <Autocomplete
            value={selectedTimezone}
            onChange={handleTimezoneChange}
            options={USATimezones}
            size="small"
            disableClearable
            getOptionLabel={(option) => getTimezoneOffsetLabel(option)}
            sx={{
              width: '100%',
              fontSize: {
                xs: '0.8rem',
                sm: '0.9rem',
                md: '1rem',
              },
              backgroundColor: 'white',
              borderRadius: 1,
              '& .MuiInputBase-root': {
                padding: {
                  xs: '6px',
                  sm: '8px',
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: {
                  xs: '0.7rem',
                  sm: '0.8rem',
                },
              },
            }}
            ListboxProps={{
              style: {
                maxHeight: '220px',
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Timezone"
                variant="outlined"
                error={isTimezoneMismatch()}
              />
            )}
          />
          {isTimezoneMismatch() && (
            <Tooltip title="Your detected timezone doesn't match the selected timezone. Click to resolve.">
              <IconButton
                size="small"
                onClick={() => {
                  setModalTimezone(selectedTimezone); // Initialize modal with current selection
                  setShowMismatchModal(true);
                }}
                sx={{
                  position: 'absolute',
                  left: { xs: 'auto', sm: '-20%' },
                  right: { xs: '-35%', sm: 'auto' },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#ff9800',
                  marginRight: '10px',
                  p: 0.5,
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  zIndex: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 152, 0, 0.2)',
                  },
                }}
                tabIndex={0}
              >
                <WarningIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Timezone Mismatch Modal */}
      <Dialog
        open={showMismatchModal}
        onClose={() => {
          setModalTimezone(null); // Reset modal timezone
          setShowMismatchModal(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '16px',
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon sx={{ color: '#ff9800' }} />
            <Typography variant="h6">Timezone Mismatch Detected</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {geoTimezone ? (
              `Your detected timezone (${getGeoTimezoneDisplayName()}) doesn't match the currently selected timezone.`
            ) : (
              'Unable to detect your timezone automatically. Please verify your timezone selection is correct.'
            )}
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {geoTimezone ? (
              'This mismatch could affect the accuracy of time-sensitive operations. Would you like to update your timezone to match your detected location?'
            ) : (
              'If your timezone selection is incorrect, it could affect the accuracy of time-sensitive operations. Please ensure you have selected the correct timezone for your location.'
            )}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Current Timezone: {selectedTimezone ? getTimezoneOffsetLabel(selectedTimezone) : 'None'}
            </Typography>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {isDetectedTimezoneInList() ? 'Recommended Timezone' : 'Detected Timezone'}: {getGeoTimezoneDisplayName()}
            </Typography>
            {!isDetectedTimezoneInList() && geoTimezone && (
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Note: Your detected timezone ({geoTimezone}) is not in our supported timezone list.
              </Typography>
            )}
            {!geoTimezone && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Note: Timezone detection failed. This could be due to browser permissions or network issues.
              </Typography>
            )}
          </Box>
          
          {/* Timezone Selection Dropdown */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Select Your Timezone:
            </Typography>
            <Autocomplete
              value={modalTimezone}
              onChange={(event, newValue) => {
                if (newValue) {
                  setModalTimezone(newValue);
                }
              }}
              options={USATimezones}
              size="small"
              disableClearable
              getOptionLabel={(option) => getTimezoneOffsetLabel(option)}
              sx={{
                width: '100%',
                fontSize: '0.9rem',
                backgroundColor: 'white',
                borderRadius: 1,
                '& .MuiInputBase-root': {
                  padding: '8px',
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.8rem',
                },
              }}
              ListboxProps={{
                style: {
                  maxHeight: '200px', 
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Choose Timezone"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setModalTimezone(null); // Reset modal timezone
              setShowMismatchModal(false);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (modalTimezone) {
                handleModalTimezoneChange(modalTimezone);
              }
            }}
            variant="contained"
            color="primary"
          >
            Update Timezone
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
